import React, { useEffect, useState } from 'react';

import {
  ActionIcon,
  Group,
  LoadingOverlay,
  Skeleton,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEdit, IconPrinter, IconTrashX } from '@tabler/icons-react';
import Drawer from 'components/Drawer';
import PayrollPeriodSelect from 'components/form-fields/select-fields/PayrollPeriodSelect';
import KiloButton from 'components/KiloButton';
import Menu from 'components/Menu';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import usePayrollEvents from 'hooks/payrollEvent/usePayrollEvents';
import { CellProps } from 'react-table';
import { PayrollEventItem, PayrollEventRetrieve, PayrollReport } from 'types';
import { capitalizeFirstLetter, formatPayrollEventAttendees } from 'utils/formatters';

import ModalBulkPayrollExport from './ModalBulkPayrollExport';
import ModalOtherWorkEventDelete from './ModalOtherWorkEventDelete';
import ModalOtherWorkEventEdit from './ModalOtherWorkEventEdit';
import ModalTotalSessionValueBreakdown from './ModalTotalSessionValueBreakdown';

export type SessionValueBreakdown = {
  genesisEventName: string;
  genesisEventType: string;
  payrollEventItems: PayrollEventItem[];
};

const DrawerStaffDetailPayoutReport = ({
  staff,
  initialPayrollPeriod,
  setInitialPayrollPeriod,
  opened,
  close,
  permission = true,
}: {
  staff?: PayrollReport;
  initialPayrollPeriod: string | null;
  setInitialPayrollPeriod: React.Dispatch<React.SetStateAction<string | null>>;
  opened: boolean;
  close: () => void;
  permission?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { formatCurrency, timeFormat } = useLocalization();

  const [ordering, setOrdering] = useState('date');
  const [ascending, setAscending] = useState(true);
  const [payrollPeriod, setPayrollPeriod] = useState(initialPayrollPeriod);
  const [tableData, setTableData] = useState<PayrollEventRetrieve[]>([]);
  const [payrollEvent, setPayrollEvent] = useState<PayrollEventRetrieve | undefined>();
  const [payrollEventBreakdown, setPayrollEventBreakdown] = useState<SessionValueBreakdown | undefined>();

  const [
    isEditOtherWorkEventModalOpened,
    { open: openEditOtherWorkEventModal, close: closeEditOtherWorkEventModal },
  ] = useDisclosure(false);
  const [
    isDeleteOtherWorkEventModalOpened,
    { open: openDeleteOtherWorkEventModal, close: closeDeleteOtherWorkEventModal },
  ] = useDisclosure(false);
  const [isBulkExportModalOpened, { open: openBulkExportModal, close: closeBulkExportModal }] =
    useDisclosure(false);
  const [
    isTotalSessionValueBreakdownModalOpen,
    { open: openTotalSessionValueBreakdownModalOpen, close: closeTotalSessionValueBreakdownModalOpen },
  ] = useDisclosure(false);

  const { data, hasNextPage, fetchNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    usePayrollEvents({
      staffId: staff?.staffId,
      payrollPeriod,
      limit: '10000',
      ordering: ascending ? ordering : `-${ordering}`,
      enabled: !!payrollPeriod,
    });

  const handleHeaderClick = (header: string): void => {
    setOrdering(header);
    setAscending(a => !a);
  };

  const handleEditPayrollEvent = (pe: PayrollEventRetrieve): void => {
    setPayrollEvent(pe);
    openEditOtherWorkEventModal();
  };

  const handleCloseEditOtherWorkEventModal = (): void => {
    closeEditOtherWorkEventModal();
    setPayrollEvent(undefined);
  };

  const handleCloseDeleteOtherWorkEventModal = (): void => {
    closeDeleteOtherWorkEventModal();
    setPayrollEvent(undefined);
  };

  const handleDeletePayrollEvent = (pe: PayrollEventRetrieve): void => {
    setPayrollEvent(pe);
    openDeleteOtherWorkEventModal();
  };

  const handleOpenPayrollReportBreakdownModal = (pe: PayrollEventRetrieve): void => {
    setPayrollEventBreakdown({
      genesisEventName: pe.genesisEventName,
      genesisEventType: pe.genesisEventType,
      payrollEventItems: pe.payrollEventItems,
    });
    openTotalSessionValueBreakdownModalOpen();
  };

  const handleClose = (): void => {
    setInitialPayrollPeriod(initialPayrollPeriod);
    setPayrollPeriod(initialPayrollPeriod);
    close();
  };

  const getPayroll = (): JSX.Element => {
    if (isLoading) {
      return <Skeleton height='1.5rem' width='5rem' />;
    }
    if (isError) {
      return (
        <Text color='dimmed' fw='bold' size='1.5rem'>
          Error
        </Text>
      );
    }
    if (isSuccess && data) {
      return (
        <Text color='dimmed' fw='bold' size='1.5rem'>
          {`Total Payout: ${formatCurrency(
            tableData.reduce((sum, obj) => sum + obj.payoutAmount, 0) / 100,
          )}`}
        </Text>
      );
    }
    return <></>;
  };

  const hasPayrollEventItems = (payrollEventRow: PayrollEventRetrieve): boolean => {
    return payrollEventRow.payrollEventItems.length > 0;
  };

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  useEffect(() => {
    if (initialPayrollPeriod) {
      setPayrollPeriod(initialPayrollPeriod);
    }
  }, [initialPayrollPeriod]);

  const columns = [
    {
      Header: 'Event',
      accessor: 'genesisEventName' as const,
    },
    {
      Header: 'Event Type',
      accessor: 'genesisEventType' as const,
      Cell: ({ value, row }: CellProps<PayrollEventRetrieve>) => (
        <>
          <Text sx={{ whiteSpace: 'nowrap' }}>{capitalizeFirstLetter(value)}</Text>
          <Text sx={{ whiteSpace: 'nowrap' }}>{formatPayrollEventAttendees(row.original)}</Text>
        </>
      ),
    },
    {
      Header: 'Role',
      accessor: 'roleName' as const,
    },
    {
      Header: 'Pay Rate',
      accessor: 'payRateName' as const,
    },
    {
      Header: 'Payout',
      accessor: 'payoutAmount' as const,
      Cell: ({ value, row }: CellProps<PayrollEventRetrieve>) => {
        if (hasPayrollEventItems(row.original)) {
          return (
            <KiloButton
              rootVariantType='flat'
              variant='subtle'
              onClick={() => handleOpenPayrollReportBreakdownModal(row.original)}
            >
              {formatCurrency(value / 100)}
            </KiloButton>
          );
        }
        return formatCurrency(value / 100);
      },
    },
    {
      Header: 'Date',
      accessor: 'date' as const,
    },
    {
      Header: 'Start',
      accessor: 'time' as const,
      Cell: ({ value }: CellProps<PayrollEventRetrieve>) => {
        dayjs.extend(customParseFormat);
        return dayjs(value, 'HH:mm:ss').format(timeFormat);
      },
    },
    {
      Header: 'Duration',
      accessor: 'duration' as const,
      Cell: ({ value }: CellProps<PayrollEventRetrieve>) => <>{value} min</>,
    },
    {
      Header: 'Reserved',
      accessor: 'athletesReserved' as const,
    },
    {
      Header: 'Attended',
      accessor: 'athletesAttended' as const,
    },
    {
      Header: 'Late Cancel',
      accessor: 'athletesLateCancelled' as const,
      Cell: ({ value }: CellProps<PayrollEventRetrieve>) => <>{value || 0}</>,
    },
    {
      Header: 'No Show',
      accessor: 'athletesNoShow' as const,
      Cell: ({ value }: CellProps<PayrollEventRetrieve>) => <>{value || 0}</>,
    },
    {
      id: 'action',
      Cell: ({ row }: CellProps<PayrollEventRetrieve>) =>
        row.original.genesisEventType === 'OTHER' ? (
          <Group position='right'>
            <Menu
              disabled={!permission || row.original.payrollPeriod.isClosed}
              position='bottom-end'
              data-cy='staffPayrollReportDetailActionButton'
              items={[
                {
                  title: 'Edit',
                  onClick: () => handleEditPayrollEvent(row.original),
                  icon: <IconEdit size={theme.fontSizes.sm} />,
                },
                {
                  title: 'Delete',
                  onClick: () => handleDeletePayrollEvent(row.original),
                  icon: <IconTrashX size={theme.fontSizes.sm} />,
                  color: 'red',
                },
              ]}
            />
          </Group>
        ) : null,
    },
  ];

  return (
    <Drawer
      opened={opened}
      closeOnClickOutside
      onClose={handleClose}
      title={<Title order={3}>Payroll Report: {staff?.staffName}</Title>}
      trapFocus
      size={1200}
      padding={'0'}
    >
      <LoadingOverlay visible={!staff || isLoading} />
      <Group noWrap position='apart' px='lg' py='sm'>
        {getPayroll()}
        <Group noWrap position='right'>
          <PayrollPeriodSelect value={payrollPeriod} onChange={setPayrollPeriod} miw='20rem' />
          <Tooltip label='Export payroll report'>
            <ActionIcon onClick={openBulkExportModal} data-cy='coachBulkPayrollExportButton'>
              <IconPrinter />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={[
          'genesisEventType',
          'genesisEventName',
          'roleName',
          'payRateName',
          'payoutAmount',
          'date',
          'time',
          'duration',
          'athletesAttended',
          'athletesReserved',
        ]}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
      />
      <ModalOtherWorkEventDelete
        opened={isDeleteOtherWorkEventModalOpened}
        handleClose={handleCloseDeleteOtherWorkEventModal}
        payrollEvent={payrollEvent}
      />
      <ModalOtherWorkEventEdit
        opened={isEditOtherWorkEventModalOpened}
        handleClose={handleCloseEditOtherWorkEventModal}
        payrollEvent={payrollEvent}
      />
      <ModalBulkPayrollExport
        opened={isBulkExportModalOpened}
        handleClose={closeBulkExportModal}
        staffId={staff?.staffId}
        staffName={staff?.staffName}
      />
      <ModalTotalSessionValueBreakdown
        handleClose={closeTotalSessionValueBreakdownModalOpen}
        opened={isTotalSessionValueBreakdownModalOpen}
        breakdownData={payrollEventBreakdown}
      />
    </Drawer>
  );
};

export default DrawerStaffDetailPayoutReport;
